import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, filter, finalize, map, shareReplay, switchMap, take, timeout } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  GetContactByPhoneRequest,
  StartWhatsappAttendanceRequest,
} from './start-whatsapp-attendance-request';
import { generateRequestTimeLogHeader } from 'src/app/core/interceptors/log.interceptor';
import { LiveChatStateService } from 'src/app/modules/live-chat/services/live-chat-state.service';
import { LoggerService } from 'src/app/core/services/logger.service';
import { LogEvent } from 'src/app/core/enums/log-event';

@Injectable({
  providedIn: 'root',
})
export class WhatsappStartAttendanceService {
  private static readonly SOCKET_RECONNECT_TIMEOUT_MS = 10000;

  private readonly pendingRequestsByKey = new Map<string, Observable<any>>();

  constructor(
    private httpClient: HttpClient,
    private liveChatState: LiveChatStateService,
    private logger: LoggerService,
  ) {}

  startWhatsappAttendance(request: StartWhatsappAttendanceRequest) {
    return this.httpClient.post(
      `${environment.externalUrls.bff}/whatsapp/start-attendance`,
      request,
      { headers: generateRequestTimeLogHeader('start-attendance') }
    );
  }

  getContactByPhone(request: GetContactByPhoneRequest) {
    const key = this.buildPendingRequestKey(request);
    const pending = this.pendingRequestsByKey.get(key);
    if (pending) {
      return pending;
    }

    const request$ = this.waitForSocketConnection().pipe(
      switchMap((isConnected) =>
        isConnected ? this.fetchContactByPhone(request) : of(null)
      ),
      finalize(() => this.pendingRequestsByKey.delete(key)),
      shareReplay(1)
    );

    this.pendingRequestsByKey.set(key, request$);

    return request$;
  }

  private buildPendingRequestKey(request: GetContactByPhoneRequest): string {
    const sortedCompaniesIds = [...request.companiesIds].sort().join(',');
    return `${request.phone}|${sortedCompaniesIds}`;
  }

  private waitForSocketConnection(): Observable<boolean> {
    if (this.liveChatState.currentSocketConnectionId) {
      return of(true);
    }

    return this.liveChatState.socketConnectionId$.pipe(
      filter((connectionId) => !!connectionId),
      take(1),
      map(() => true),
      timeout(WhatsappStartAttendanceService.SOCKET_RECONNECT_TIMEOUT_MS),
      catchError(() => {
        this.logger.log(LogEvent.LIVECHAT_CONTACT_LOOKUP_SOCKET_WAIT_TIMEOUT, {
          waitedMs: WhatsappStartAttendanceService.SOCKET_RECONNECT_TIMEOUT_MS,
        });
        return of(false);
      })
    );
  }

  private fetchContactByPhone(request: GetContactByPhoneRequest) {
    const url = new URL(
      `${environment.externalUrls.bff}/contact/with-last-attendance`
    );
    return this.httpClient.get(url.toString(), {
      params: {
        phone: request.phone,
        companiesIds: request.companiesIds.join(','),
      },
      headers: generateRequestTimeLogHeader('contact-with-last-attendance'),
    });
  }
}
